/**
 * @file destinations.api.js
 * @module DestinationsAPI
 * @description HTTP API wrapper for destination-related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

/**
 * API methods related to Destinations.
 * @namespace DestinationsAPI
 */
export const DestinationsAPI = {
    /**
     * Get destinations options for selects and filters.
     *
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to destinations options.
     */
    getDestinationsOptions: (token = "") => {
        return axiosInstance(token).get(`/destinations-options`);
    },

    /**
     * Create a new destination.
     *
     * @param {Object} payload - Destination payload to be sent in the request body.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the created destination.
     */
    createDestination: (payload, token = "") => {
        return axiosInstance(token).post(`/destinations`, payload);
    },

    /**
     * Retrieve a paginated list of destinations.
     *
     * @param {number} [limit=20] - Number of items per page.
     * @param {number} [page=1] - Page number to retrieve.
     * @param {string} [query=""] - Optional URL-encoded query string to filter results (e.g. 'q=foo').
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to a paginated destinations response.
     */
    getDestinations: async (limit = 20, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(
            `/destinations?limit=${limit}&page=${page}${query ? `&${query}` : ""}`
        );
    },

    /**
     * Retrieve a single destination by its identifier.
     *
     * @param {string|number} id - Destination identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the destination object.
     */
    getDestinationById: (id, token = "") => {
        return axiosInstance(token).get(`/destinations/${id}`);
    },

    /**
     * Update a destination by its identifier.
     *
     * @param {string|number} id - Destination identifier.
     * @param {Object} payload - Partial or full destination payload to update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the updated destination.
     */
    updateDestinationById: (id, payload, token = "") => {
        return axiosInstance(token).put(`/destinations/${id}`, payload);
    },

    /**
     * Delete a destination by its identifier.
     *
     * @param {string|number} id - Destination identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the delete result.
     */
    deleteDestinationById: (id, token = "") => {
        return axiosInstance(token).delete(`/destinations/${id}`);
    },

    /**
     * Check whether a destination slug is duplicated within a given country.
     *
     * @param {string} destinationSlug - Slug for the destination to check.
     * @param {string} countrySlug - Country slug where the destination is located.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to an object indicating duplication status.
     */
    checkDestinationSlugIsDuplicate: (countrySlug, destinationSlug, token = "") => {
        return axiosInstance(token).get(
            `/destinations/${countrySlug}/${destinationSlug}/isDuplicate`
        );
    },

    /**
     * Get time zone information for a geographical coordinate at a given timestamp.
     *
     * @param {number} lat - Latitude in decimal degrees.
     * @param {number} lng - Longitude in decimal degrees.
     * @param {number|string} timestamp - POSIX timestamp (seconds) or string accepted by the API.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to time zone data for the coordinates.
     */
    getTimezoneByCoordinates: (countrySlug, destinationSlug, payload, token = "") => {
        return axiosInstance(token).post(`/destinations/${countrySlug}/${destinationSlug}/timezone`, payload);
    },

    /**
     * Change the status of a destination by its identifier.
     *
     * @param {string|number} id - Destination identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the updated destination status.
     */
    changeDestinationStatusById: (id, token = "") => {
        return axiosInstance(token).patch(`/destinations/${id}/status`);
    }
}